import React, { startTransition, useEffect, useRef } from 'react';

import { bindAll } from 'bind-event-listener';
import { useIntl } from 'react-intl';

import { aiExperienceApplicationMessages } from '@atlaskit/editor-common/ai-messages';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { EditorAppearance, ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import { isEmptyDocument } from '@atlaskit/editor-common/utils';
import type { GetMentionNameDetails } from '@atlaskit/editor-mention-types/types';
import type { NodeType } from '@atlaskit/editor-prosemirror/model';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import UFOSegment from '@atlaskit/react-ufo/segment';
import { usePublish } from '@atlaskit/rovo-triggers/main';
import type { StreamFlowPacer } from '@atlassian/editor-ai-common/api/stream-flow-pacer';
import type { EditorAgent } from '@atlassian/generative-ai-modal/utils/agents';

import type { AIPlugin } from '../../../aiPluginType';
import type { EditorPluginAIProvider, EndExperience } from '../../../types/types';
import {
	AIExperienceCommonDataContext,
	useAIExperienceCommonDataContext,
} from '../../hooks/useAIExperienceCommonData';
import type { AIExperienceCommonDataContextData } from '../../hooks/useAIExperienceCommonData';
import { useAIExperienceService } from '../../hooks/useAIExperienceService';
import { CONFIG_ITEM_KEYS } from '../../prebuilt/config-items/config-item-keys';
import type {
	AnalyticsContext,
	EditorPluginAIConfigItem,
	SelectionType,
} from '../../prebuilt/config-items/config-items';
import { createFreeGenerateEditorAgentConfigItem } from '../../prebuilt/config-items/prompts/free-generate-editor-agent/free-generate-editor-agent';
import type {
	InvokedFrom,
	InvokedFromTriggerMethod,
} from '../../utils/analytics/analyticsFlowTypes';
import {
	AnalyticsFlowContextProvider,
	useAnalyticsFlow,
	useCreateAnalyticsFlow,
} from '../../utils/analytics/analyticsFlowUtils';
import { useFireAIAnalyticsEvent } from '../../utils/analytics/utils';
import { shouldDiscardEndExperienceImmediately } from '../../utils/discard-handling';
import { FAILURE_REASON } from '../../utils/errors';
import { getLoadingMessage } from '../../utils/request/get-loading-message';
import { useStreamFlowPacerRef } from '../../utils/request/stream-flow-pacer';
import { getSelectionCategory } from '../../utils/selection';
import { createStreaming } from '../../utils/streaming';
import type {
	AIExperienceMachineContext,
	AIExperienceMachineEvent,
	AIExperienceMachineState,
	Positions,
} from '../../utils/xstate/get-ai-experience-service';
import { ModalRegionErrorBoundary } from '../ExperienceApplicationErrorBoundary/ExperienceApplicationErrorBoundary';
import { UFOLoadingStatusTracker } from '../UFOLoadingStatusTracker/UFOLoadingStatusTracker';

import { DiscardScreenWithLogic } from './screens-with-logic/DiscardScreenWithLogicLegacy';
import { ErrorScreenWithLogic } from './screens-with-logic/ErrorScreenWithLogic';
import { InvalidInputScreenWithLogic } from './screens-with-logic/InvalidInputScreenWithLogic';
import { LoadingScreenWithLogic } from './screens-with-logic/LoadingScreenWithLogicLegacy';
import { PreviewScreenWithLogic } from './screens-with-logic/PreviewScreenWithLogic';
import { UserInputCommandPaletteWithLogic } from './screens-with-logic/UserInputCommandPaletteWithLogic';

type ExperienceApplicationProps = {
	aiGroupId?: string;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	appearance: EditorAppearance;
	canInlineStreamChanges: () => boolean;
	configItem: EditorPluginAIConfigItem;
	context: AIExperienceMachineContext;
	defaultAgent?: EditorAgent;
	editorPluginAIProvider: EditorPluginAIProvider;
	// all props under here may change based on how we wire things together
	editorView: EditorView;
	/**
	 * Called to end the experience
	 *
	 * Note: the ExperienceApplication will do additional cleanup work after calling this in
	 * its react unmount cleanup.
	 */
	endExperience: EndExperience;
	/** Engagement platform plugin API for starting and stopping messages. */
	engagementPlatformApi?: AIExperienceCommonDataContextData['engagementPlatformApi'];
	getMentionNameDetails?: GetMentionNameDetails;
	/**
	 * Option to hide the loading screen
	 */
	hideLoadingScreen?: boolean;
	/**
	 * Initial positions of the user's selection
	 */
	initialPositions?: Positions;
	initialPrompt?: string;
	interactionSessionId?: string;
	lastTriggeredFrom?: InvokedFrom;

	/**
	 * Confidence score will be used passed in analytics events.
	 * It is currently only set when "Improve writing" is triggered from the
	 * 	"Improve writing" nudge.
	 */
	nudgeMetrics?: Record<string, number | undefined>;
	/**
	 * For empty selections, the start and end will be the same
	 */
	positions: Positions;
	providerFactory: ProviderFactory;
	// Context by `useAIExperienceService` which creates the xstate machine and passes here
	send: (event: AIExperienceMachineEvent) => void;
	state: AIExperienceMachineState;
	triggeredFor?: NodeType;
	triggerMethod?: InvokedFromTriggerMethod;
	willAISuggestEmoji?: boolean;
};

type ExperienceApplicationStateManagerProps = {
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	context: AIExperienceMachineContext;
	initialPrompt?: string;
	modalRef?: React.RefObject<HTMLDivElement>;
	state: AIExperienceMachineState;
	streamFlowPacer: StreamFlowPacer | null;
};

export function ExperienceApplicationStateManager(
	props: ExperienceApplicationStateManagerProps,
): React.JSX.Element | null {
	const { api } = props;
	/**
	 * Note -- because we are on an old version of xstate -- when adding new states
	 * they will not automatically be typed.
	 *
	 * You need to add these in the `AIExperienceMachineState` type in the
	 * `get-experience-service.ts` file
	 **/
	const state = props.state;
	/**
	 * Note -- because we are on an old version of xstate -- when adding new events
	 * they will not automatically be typed.
	 *
	 * You need to add these in the `AIExperienceMachineEvent` type in the
	 * `get-experience-service.ts` file
	 **/
	const context = props.context;
	const aiExperienceCommonData = useAIExperienceCommonDataContext();

	const { formatMessage } = useIntl();

	const publish = usePublish('ai-mate');

	const autoAcceptAIChanges = aiExperienceCommonData.canInlineStreamChanges();

	React.useEffect(() => {
		const send = aiExperienceCommonData.sendToAIExperienceMachine;
		if (send && state === 'started state') {
			send({ type: 'kick off' });
		}
	}, [state, aiExperienceCommonData.sendToAIExperienceMachine]);

	/**
	 * Used to pass the backendModel to analytics and
	 * feedback submission metadata
	 */
	const backendModel = context?.configItem?.getBackendModel?.(!!context?.latestPromptResponse);

	const analyticsFlow = useAnalyticsFlow();

	React.useEffect(() => {
		analyticsFlow.updateCommonAttributes({
			backendModel,
			channelId: context.channelId,
			...analyticsFlow.getUserFlowCommonAttributes(context.configItem),
			autoAcceptAIChanges,
			inlineStreamingAvailable: false,
		});
	}, [analyticsFlow, backendModel, context.channelId, context.configItem, autoAcceptAIChanges]);

	const aiLifeCycleDynamicAttributesGetter = context.aiLifeCycleDynamicAttributesGetter;
	React.useEffect(() => {
		if (aiLifeCycleDynamicAttributesGetter) {
			analyticsFlow.registerAiLifeCycleDynamicAttributesGetter(aiLifeCycleDynamicAttributesGetter);
		}
	}, [analyticsFlow, aiLifeCycleDynamicAttributesGetter]);

	// Note -- the intention is to move the creation of the state machine higher
	// and for the ExperienceApplication (react component that appears in the modal)
	// to only be started once the machine is in the modal active state.
	if (state === 'started state') {
		return null;
	}

	const isModalActiveState = typeof state !== 'string' && state['modal active state'];

	if (!isModalActiveState) {
		throw new Error('AI machine state is not: modal active state or modal error state');
	}

	const experienceState = state['modal active state']['experience'];

	if (
		typeof experienceState !== 'string' &&
		'error state' in experienceState &&
		experienceState['error state'] === 'active'
	) {
		if (context.apiErrorInfo.failureReason === FAILURE_REASON.NO_TEXTUAL_CONTENT) {
			return (
				<InvalidInputScreenWithLogic
					errorInfo={context.apiErrorInfo}
					contextStatistics={context.contextStatistics}
				/>
			);
		}
		return <ErrorScreenWithLogic errorInfo={context.apiErrorInfo} />;
	}

	if (
		typeof state['modal active state'] === 'object' &&
		state['modal active state']['discard'] === 'active'
	) {
		return (
			<DiscardScreenWithLogic
				state={experienceState}
				channelId={context.channelId}
				editCount={context.editCount}
				retryPromptCount={context.retryPromptCount}
				history={context.responseHistory}
				latestPrompt={context.latestPrompt}
				onceDiscard={context.onceDiscard}
				streamFlowPacer={props.streamFlowPacer}
				api={api}
			/>
		);
	}

	if (typeof experienceState === 'string' && experienceState === 'command palette state') {
		return (
			<UserInputCommandPaletteWithLogic context={context} initialPrompt={props.initialPrompt} />
		);
	}

	if (typeof experienceState === 'object' && 'loading state' in experienceState) {
		const loadingState = experienceState['loading state'];
		if (loadingState === 'loading') {
			/**
			 * Short title for the submenu if the user selects a submenu via a parent (`Change tone` -> `Neutral`)
			 * If the user selects a submenu directly, this will be empty (search and select `Change tone to Neutral`)
			 */
			const childShortTitle =
				context.parentPresetPromptLabel && context.configItem.nestingConfig
					? formatMessage(context.configItem.nestingConfig.shortTitle)
					: '';
			const loadingMessage = getLoadingMessage({ status: context?.loadingStatus, formatMessage });

			return (
				<>
					<LoadingScreenWithLogic
						parentPresetPromptLabel={context.parentPresetPromptLabel}
						childShortTitle={childShortTitle}
						title={
							loadingMessage
								? loadingMessage
								: formatMessage(aiExperienceApplicationMessages.loadingTitle)
						}
						markdown={context.markdown}
						adfResponse={context.adfResponse}
						latestPrompt={context.latestPrompt}
						promptTrigger={context.promptTrigger}
						refinementCount={context.refinementCount}
						latestPromptRetried={context.latestPromptRetried}
						idMap={context.idMap}
						contextStatistics={context.contextStatistics}
						hideLoadingScreen={aiExperienceCommonData.hideLoadingScreen}
					/>
					<UFOLoadingStatusTracker loadingStatus={context.loadingStatus} />
				</>
			);
		}
	}

	if (typeof experienceState !== 'string' && 'review state' in experienceState) {
		const action = context.rovoActions ? context.rovoActions[context.selectionType] : undefined;
		return (
			<PreviewScreenWithLogic
				aiExperienceMachineContext={context}
				isInputActive={experienceState['review state'] === 'refinement'}
				backendModel={backendModel}
				rovoAction={action}
				rovoPublish={publish}
			/>
		);
	}

	throw new Error('unhandled state');
}

type ExperienceApplicationStateWrapperProps = ExperienceApplicationProps & {
	/** The analytics context for the experience */
	analyticsContext: AnalyticsContext;
};

function canReplaceBaseGenerate(aiProvider: EditorPluginAIProvider) {
	return aiProvider.isRovoEnabled && aiProvider.useFreeGenerateEditorAgent;
}

function focusEditor(editorView: EditorView) {
	if (!editorView.hasFocus()) {
		editorView.focus();
	}
}

// document.body will be focused when we switch from one screen to another
// (can be triggered by clicking button or pressing Esc key)
// which will make pressing Esc not working because we're listening Esc on editor
// so we should focus editor when it's not focused
function useFocusEditor({
	editorView,
	cancelRovoPrompt,
}: {
	cancelRovoPrompt?: () => void;
	editorView: EditorView;
}) {
	useEffect(() => {
		if (cancelRovoPrompt) {
			focusEditor(editorView);
		}
	}, [cancelRovoPrompt, editorView]);
}

function ExperienceApplicationStateWrapper(props: ExperienceApplicationStateWrapperProps) {
	const { api, editorView, endExperience } = props;
	const intl = useIntl();
	const modalRef = React.useRef<HTMLDivElement>(null);

	const positions = props.positions;
	const isEmpty = positions[0] === positions[1];
	const selectionType: SelectionType = isEmpty ? 'empty' : 'range';

	/**
	 * baseGenerate configItem is used as default configItem in command palette.
	 * Currently for every product, "freeGenarete" is used as baseGenerate.
	 * Recently, "useFreeGenerateEditorAgent" is introduced to allow
	 * 	freeGenerateEditorAgent configItem to be used as baseGenerate.
	 *  freeGenerateEditorAgent is like freeGeneerate, but it uses
	 * 	rovo agent instead of standard AI prompt.
	 *
	 * Can't set "freeGenerateEditorAgent" directly as baseGenerate in prebuilt config,
	 * 	because creating configItem requires agent to be passed in.
	 * But agents are not available at compile time or event when plugin is loaded.
	 * So we need to swap baseGenerate here.
	 */
	let configItem = props.configItem;
	let baseGenerate = props.editorPluginAIProvider.baseGenerate;
	if (props.defaultAgent && canReplaceBaseGenerate(props.editorPluginAIProvider)) {
		baseGenerate = createFreeGenerateEditorAgentConfigItem({
			agent: props.defaultAgent,
			product: props.editorPluginAIProvider.product,
			isInDocumentStreamingEnabled: props.editorPluginAIProvider.isInDocumentStreamingEnabled,
		});

		if (props.configItem.key === CONFIG_ITEM_KEYS.FREE_GENERATE) {
			configItem = baseGenerate;
		}
	}

	const isUserScroll = useRef<boolean>(false);
	const isProgrammaticScroll = useRef<boolean>(true);

	const streamFlowPacerRef = useStreamFlowPacerRef();

	const analyticsFlow = useAnalyticsFlow();
	const fireAIAnalyticsEvent = useFireAIAnalyticsEvent();
	const streamingHandler = createStreaming({
		fireAIAnalyticsEvent,
		aiInteractionID: analyticsFlow.getLastAiInteractionId(),
		streamFlowPacer: streamFlowPacerRef.current,
	});

	const { context, state, send } = useAIExperienceService({
		context: {
			analyticsContext: props.analyticsContext,
			aiProvider: props.editorPluginAIProvider,
			intl,
			editorView: props.editorView,
			positions: positions,
			isEmpty,
			selectionType,
			baseGenerate,
			configItem,
			aiLifeCycleDynamicAttributesGetter:
				props.editorPluginAIProvider?.aiLifeCycleDynamicAttributesGetter,
			markdown: '',
			latestPromptResponse: undefined,
			editCount: 0,
			refinementCount: 0,
			retryPromptCount: 0,
			latestPromptRetried: false,
			userInput: '',
			apiErrorInfo: {},
			responseHistory: { positionFromEnd: 0, entries: [], totalSize: 0, traversals: 0 },
			channelId: undefined,
			getMentionNameDetails: props.getMentionNameDetails,
			triggeredFor: props.triggeredFor,
			usePageKnowledge: true,
			endExperience: props.endExperience,
			streamingHandler,
		},
	});

	React.useEffect(() => {
		if (props.defaultAgent && canReplaceBaseGenerate(props.editorPluginAIProvider)) {
			const newBaseGenerate = createFreeGenerateEditorAgentConfigItem({
				agent: props.defaultAgent,
				product: props.editorPluginAIProvider.product,
				isInDocumentStreamingEnabled: props.editorPluginAIProvider.isInDocumentStreamingEnabled,
			});
			send({
				type: 'update baseGenerate and configItem',
				baseGenerate: newBaseGenerate,
				configItem: newBaseGenerate,
			});
		}
	}, [props.defaultAgent, props.editorPluginAIProvider, send]);

	const [start, end] = props.positions;
	React.useEffect(() => {
		// Note -- the intention is to have the state machine created at a higher
		// level, and have the positions updated without being passed into this react
		// component.
		send({ type: 'update positions', positions: [start, end] });
		// Separate start/end as React will recognise tuples recreated
		// as a change, even if the values don't change
	}, [send, start, end]);

	useFocusEditor({ editorView });

	React.useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		/**
		 * KeyDown (as opposed to KeyPress) event is used to handle the escape key so that
		 * other escape key handlers will not override this behaviour.
		 * Additionally this aligns with other escape listeners e.g. for closing typeahead popup.
		 */
		function handleEscapeKeyDown(event: KeyboardEvent) {
			// Exit early if the key is not Escape
			if (event.key !== 'Escape') {
				return;
			}

			// Continue only if the AI modal is open / in active state
			if (typeof state === 'string' || !('modal active state' in state)) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();

			const modalActiveState = state?.['modal active state'];
			const experience = modalActiveState?.experience;

			if (
				typeof experience === 'object' &&
				'loading state' in experience &&
				experience['loading state'] === 'loading'
			) {
				if (modalActiveState?.discard === 'active') {
					send({ type: 'disable discard' });
				} else {
					send({ type: 'enable discard' });
				}
			} else {
				if (shouldDiscardEndExperienceImmediately(context.promptTrigger, context.responseHistory)) {
					endExperience();
					return;
				}

				send({ type: 'escape' });
			}
		}

		/**
		 * Handle KeyUp event to prevent unexpected escape behaviours when
		 * there are other escape handlers outside of component (e.g. in product)
		 */
		function handleEscapeKeyUp(event: KeyboardEvent) {
			// Exit early if the key is not Escape
			if (event.key !== 'Escape') {
				return;
			}

			// Continue only if the AI modal is open / in active state
			if (typeof state === 'string' || !('modal active state' in state)) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
		}

		// initially, editor is focused
		// when AI modal is loading and user presses ESC
		// we will show the AI modal discard screen
		// which will make AI modal to be auto focused
		// now if user presses ESC again,
		// we will return to the AI modal loading screen
		// so whenever user presses ESC, either the AI modal or the editor is focused
		// so the listener should be attached to the common ancestor of the AI Modal and the editor
		const node = modalRef.current;

		if (!node) {
			return;
		}

		return bindAll(node, [
			{ type: 'keydown', listener: handleEscapeKeyDown },
			{ type: 'keyup', listener: handleEscapeKeyUp },
		]);
	}, [
		modalRef,
		state,
		send,
		context.promptTrigger,
		context.responseHistory,
		endExperience,
		editorView,
		api,
	]);

	const [isMounted, setIsMounted] = React.useState(false);
	React.useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		startTransition(() => {
			setIsMounted(true);
		});
	}, [modalRef]);

	React.useEffect(() => {
		if (!modalRef.current) {
			return;
		}

		if (isMounted) {
			modalRef.current?.scrollIntoView({ block: 'nearest', behavior: 'auto' });
		}
	}, [isMounted, modalRef]);

	const aiExperienceCommonData = React.useMemo(
		() => ({
			appearance: props.appearance,
			// configItem can be changed within state machine, so always get it from machine context.
			configItem: context.configItem,
			editorPluginAIProvider: props.editorPluginAIProvider,
			endExperience: props.endExperience,
			lastTriggeredFrom: props.lastTriggeredFrom,
			providerFactory: props.providerFactory,
			editorView: props.editorView,
			positions: props.positions,
			triggerMethod: props.triggerMethod,
			triggeredFor: props.triggeredFor,
			sendToAIExperienceMachine: send,
			modalRef,
			isUserScroll,
			isProgrammaticScroll,
			engagementPlatformApi: props.engagementPlatformApi,
			includeKnowledgeFromCurrentPage: context.usePageKnowledge,
			canInlineStreamChanges: props.canInlineStreamChanges,
			hideLoadingScreen: props.hideLoadingScreen,
			aiSessionId: props.interactionSessionId,
		}),
		[
			context.configItem,
			context.usePageKnowledge,
			props.editorView,
			props.editorPluginAIProvider,
			props.appearance,
			props.endExperience,
			props.lastTriggeredFrom,
			props.providerFactory,
			props.positions,
			props.triggerMethod,
			props.triggeredFor,
			props.engagementPlatformApi,
			props.canInlineStreamChanges,
			send,
			modalRef,
			isUserScroll,
			isProgrammaticScroll,
			props.hideLoadingScreen,
			props.interactionSessionId,
		],
	);

	React.useEffect(() => {
		analyticsFlow.updateCommonAttributes({ traceIds: context.traceIds });
	}, [analyticsFlow, context.traceIds]);

	return (
		<div
			// We set these to ensure that when focus initially moves to
			// an element within the modal, that context about the modal
			// (e.g. "Atlassian Intelligence Dialog") is announced too
			role="dialog"
			// ED-19787
			// We would like to use aria-labelledBy so we can label this
			// dynamically for each screen, however it's not working as expected
			// in Chrome and Firefox (works in Safari). For now we'll use the dialog
			// role and make the first element of each screen a screen-reader accessible
			// heading for the experience.
			// aria-label={intl.formatMessage(experienceMessages.dialogAriaLabel)}
			aria-label={intl.formatMessage(aiExperienceApplicationMessages.dialogAriaLabel)}
			ref={modalRef}
		>
			<AIExperienceCommonDataContext.Provider value={aiExperienceCommonData}>
				<ExperienceApplicationStateManager
					api={api}
					state={state}
					context={context}
					initialPrompt={props.initialPrompt}
					modalRef={modalRef}
					streamFlowPacer={streamFlowPacerRef.current}
				/>
			</AIExperienceCommonDataContext.Provider>
		</div>
	);
}

export function ExperienceApplication(props: ExperienceApplicationProps): React.JSX.Element {
	const analyticsFlow = useCreateAnalyticsFlow({
		invokeAttributes: {
			invokedFrom: props.lastTriggeredFrom,
			invokedFor: props.triggeredFor ? props.triggeredFor.name : 'text',
			triggerMethod: props.triggerMethod,
			experienceName: props.configItem.key,
			featureId: props.configItem.featureId,
			aiGroupId: props.aiGroupId,
			aiSessionId: props.interactionSessionId,
			willAISuggestEmoji: props.willAISuggestEmoji,
		},
		canInlineStreamChanges: () => false,
		isInDocumentStreamingEnabled: false,
	});

	// initialPositions is updated many times during in-doc streaming,
	// but we only want to update the common analytics attributes once,
	// so we check if they've already been updated
	React.useEffect(() => {
		const isInvokedFromEmptyPage = isEmptyDocument(props.editorView.state.doc);
		analyticsFlow.updateCommonAttributes({
			isInvokedFromEmptyPage,
			/**
			 * Confidence score will be used passed in analytics events.
			 * It is currently only set when "Improve writing" is triggered from the
			 * 	"Improve writing" nudge.
			 */
			nudgeMetrics: props.nudgeMetrics,
		});
		if (props.initialPositions) {
			const selectionCategory = getSelectionCategory(props.editorView, props.initialPositions);

			analyticsFlow.updateCommonAttributes({ isInvokedFromEmptyPage, selectionCategory });
		}
	}, [analyticsFlow, props.editorView, props.initialPositions, props.nudgeMetrics]);

	// finish analytics flow and fire remaining steps on unmount
	React.useEffect(() => {
		return () => {
			// have to postpone until animation frame to make sure we end analytics flow after child components unmount
			// which is where we enqueue last events
			window.requestAnimationFrame(() => {
				analyticsFlow.fireQueuedStep();
			});
		};
	}, [analyticsFlow]);

	// This makes sure that the selection preview is hidden when the experience is unmounted
	React.useEffect(() => {
		return () => {
			const tr = props.editorView.state.tr;
			tr.setMeta('hideSelectionPreview', true);
			props.editorView.dispatch(tr);
		};
	}, [props.editorView]);

	return (
		<ModalRegionErrorBoundary
			// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
			onCloseFallback={() => {
				props.endExperience();
			}}
		>
			<AnalyticsFlowContextProvider value={analyticsFlow}>
				<UFOSegment name="EditorAICommandPalette">
					<ExperienceApplicationStateWrapper
						// Ignored via go/ees005
						// eslint-disable-next-line react/jsx-props-no-spreading
						{...props}
						// eslint-disable-next-line @atlassian/perf-linting/no-unstable-inline-props -- Ignored via go/ees017 (to be fixed)
						analyticsContext={{
							aiSessionId: analyticsFlow.aiSessionId,
							aiGroupId: props.aiGroupId ?? analyticsFlow.aiSessionId,
						}}
					/>
				</UFOSegment>
			</AnalyticsFlowContextProvider>
		</ModalRegionErrorBoundary>
	);
}
